import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CanActivateChildFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CommonAdminService } from '@tanzeel/shared';

export interface CardRecoveryState {
  required: boolean; isDependent: boolean; readonly: boolean;
  recoveryId: number; parentStudentId: number; parentName: string; outstanding: number;
}
export const CARD_RECOVERY_ROUTE = '/student/r/billing/updateCard';
export const STUDENT_DISABLED_MESSAGE = 'Student account disabled Please contact Support team';

@Injectable({ providedIn: 'root' })
export class CardRecoveryService {
  current: CardRecoveryState | null = null;
  private polling = false;
  constructor(private http: HttpClient, private common: CommonAdminService, private router: Router) {}
  private options() { return { headers: { Authorization: 'Bearer ' + this.common.getValidJwtToken() } }; }
  async load(): Promise<CardRecoveryState> {
    this.current = await firstValueFrom(this.http.get<CardRecoveryState>(
      this.common.newContextPath + 'students/card-recovery/state', this.options()));
    return this.current;
  }
  async complete(receipt: any): Promise<any> {
    return firstValueFrom(this.http.post(this.common.newContextPath + 'students/card-recovery/complete', receipt, this.options()));
  }
  async createSetup(): Promise<any> {
    return firstValueFrom(this.http.post(
      this.common.newContextPath + 'students/studentcard/createsetupintent', {}, this.options()));
  }
  logout(): void { this.common.logout(null, null, null); }
  startPolling(): void {
    if (this.polling) return;
    this.polling = true;
    window.setInterval(async () => {
      if (document.hidden || !this.common.getValidJwtToken()) return;
      const role = this.common.getJwtRole(this.common.decodeJwt(this.common.getValidJwtToken())).toLowerCase();
      if (role !== 'student') return;
      try {
        const wasRequired = this.current?.required === true;
        const state = await this.load();
        if (state.required && state.isDependent) { this.disable(); return; }
        if (state.required && this.router.url.split('?')[0] !== CARD_RECOVERY_ROUTE)
          await this.router.navigateByUrl(CARD_RECOVERY_ROUTE);
        else if (wasRequired && !state.required && this.router.url.split('?')[0] === CARD_RECOVERY_ROUTE)
          window.location.replace('/#/student/d');
      } catch { /* Route/API checks remain fail-closed; polling never retries mutations. */ }
    }, 15000);
  }
  disable(): void {
    sessionStorage.removeItem('jwt_token');
    window.location.replace('/#/boarding/loginUser?cardRecoveryDisabled=1');
  }
}

export const cardRecoveryGuard: CanActivateChildFn = async (_route, navigation) => {
  const recovery = inject(CardRecoveryService);
  const common = inject(CommonAdminService);
  const router = inject(Router);
  const token = common.getValidJwtToken();
  if (!token || common.getJwtRole(common.decodeJwt(token)).toLowerCase() !== 'student') return true;
  try {
    const state = await recovery.load();
    recovery.startPolling();
    if (state.required && (state.isDependent || state.readonly)) { recovery.disable(); return false; }
    if (state.required) return navigation.url.split('?')[0] === CARD_RECOVERY_ROUTE
      ? true : router.parseUrl(CARD_RECOVERY_ROUTE);
    return true;
  } catch {
    // Do not construct an unrestricted screen when the live restriction cannot be checked.
    return false;
  }
};
